import type { CityConfig } from "../city-template/types";

export const freeportConfig: CityConfig = {
  city: "Freeport",
  state: "Maine",
  stateAbbr: "ME",
  slug: "freeport",
  route: "/cities/freeport",
  title: "Emergency & Fast Passport Renewal Freeport ME | Same-Day & Expedited | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Freeport, Maine — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Freeport, Brunswick, Yarmouth, Pownal, and Cumberland County. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Freeport, ME",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Freeport and southern Maine travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Freeport%20Maine%20downtown%20with%20L.L.Bean%20flagship%20store%20and%20historic%20brick%20buildings%2C%20southern%20Maine%20retail%20village%20with%20charming%20New%20England%20storefronts%20and%20tree-lined%20Main%20Street%2C%20classic%20Maine%20outlet%20town%20with%20American%20flag%20and%20green%20trees%2C%20summer%20afternoon%20light%2C%20professional%20travel%20photography%2C%20wide%20angle%20scenic%20view%20of%20Freeport%20Maine%20downtown%20shopping%20district&width=1800&height=700&seq=freeport-me-hero-001&orientation=landscape",
  heroImageAlt: "Freeport Maine downtown with L.L.Bean flagship store and historic retail village",
  geoPosition: "43.8570;-70.1031",
  geoPlacename: "Freeport, Maine",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "5 Custom House St, Boston, MA 02110",
    distance: "Located in Boston — approximately 120 miles (2 hours) from Freeport, ME",
  },
  nearbyCitiesZips: [
    { name: "Freeport", zips: "04032" },
    { name: "Brunswick", zips: "04011" },
    { name: "Yarmouth", zips: "04096" },
    { name: "Pownal", zips: "04069" },
    { name: "North Yarmouth", zips: "04097" },
    { name: "Durham", zips: "04222" },
    { name: "New Gloucester", zips: "04260" },
    { name: "Falmouth", zips: "04105" },
    { name: "Cumberland", zips: "04021" },
    { name: "Topsham", zips: "04086" },
    { name: "Bath", zips: "04530" },
    { name: "Portland", zips: "04101" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Freeport departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in Portland", route: "/cities/portland-me" },
    { name: "Emergency passport help in Brunswick", route: "/cities/brunswick" },
    { name: "Urgent passport services in Yarmouth", route: "/cities/yarmouth" },
    { name: "Same-day passport help in Bath", route: "/cities/bath" },
    { name: "Expedited passport services in Falmouth", route: "/cities/falmouth" },
    { name: "Passport courier services in Augusta", route: "/cities/augusta-me" },
    { name: "Rush passport help in Lewiston", route: "/cities/lewiston" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Freeport, Maine remotely via secure courier. We do not have a walk-in office in Freeport — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Freeport travelers.",
  fpcOfficeDistance: "Serving the greater Freeport and southern Maine area",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Freeport and southern Maine. From the L.L.Bean flagship store and outlet shopping district to the quiet neighborhoods of Pownal and Durham — we help Freeport travelers get their passports fast, often in 24–48 hours through our registered courier service.",
  nearbyCitiesIntro: "We serve all Freeport neighborhoods and nearby towns including Brunswick, Yarmouth, Pownal, North Yarmouth, Durham, New Gloucester, Falmouth, Cumberland, Topsham, Bath, and Portland.",
  howItWorksIntro: "Most Freeport travelers complete everything online and ship documents overnight. No need to drive to Boston or Portland. Our emergency and expedited passport process is designed for Maine travelers who need fast, reliable results.",
  mistakesIntro: "Freeport is a popular shopping destination and home to many commuters working in Portland or Brunswick. Residents and tourists often need passports at short notice. Here are the most common mistakes we see from Freeport-area travelers.",
  testimonialsIntro: "Freeport travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Freeport? Here are the most common ones we hear from southern Maine travelers.",
  statePageRoute: "/states/maine",
  statePageName: "Maine",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Freeport international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Jennifer M.",
      location: "Freeport, ME",
      rating: 5,
      text: "Realized my passport was expired 4 days before our anniversary trip to Paris. Fast Passport Center got the renewal done in 3 days. From Freeport to Paris with zero stress. These folks are the best.",
      service: "Passport Renewal",
    },
    {
      name: "Mark D.",
      location: "Brunswick, ME",
      rating: 5,
      text: "Emergency business trip to Tokyo with 48 hours notice. Fast Passport Center had my emergency passport ready in 24 hours. I shipped from the Freeport UPS Store and had it back the next day.",
      service: "Emergency Passport",
    },
    {
      name: "Rachel B.",
      location: "Yarmouth, ME",
      rating: 5,
      text: "Lost my passport right before our family cruise. Fast Passport Center handled the DS-64 form and had my replacement ready in 3 days. They even coordinated the acceptance clerk visit at the Brunswick Post Office.",
      service: "Lost Passport",
    },
    {
      name: "David S.",
      location: "Pownal, ME",
      rating: 5,
      text: "Passport renewal for our son's school trip to Quebec. Fast Passport Center helped us navigate the process and got the passport in 5 days. The shipping from our Freeport area address was easy.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "freeport-1",
      name: "Freeport Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "5 Bow St",
      city: "Freeport",
      state: "ME",
      zip: "04032",
      phone: "(207) 865-4468",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Freeport",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Main passport acceptance facility for Freeport. Call ahead for appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "freeport-2",
      name: "Brunswick Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "31 Pleasant St",
      city: "Brunswick",
      state: "ME",
      zip: "04011",
      phone: "(207) 725-6931",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "6 miles from Freeport",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Larger facility with more appointment slots. Popular for Freeport and Brunswick residents.",
      icon: "ri-mail-line",
    },
    {
      id: "freeport-3",
      name: "Yarmouth Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "15 Forest Falls Dr",
      city: "Yarmouth",
      state: "ME",
      zip: "04096",
      phone: "(207) 846-3831",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "8 miles from Freeport",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Good option for Yarmouth and North Yarmouth residents.",
      icon: "ri-mail-line",
    },
    {
      id: "freeport-4",
      name: "Portland Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "125 Forest Ave",
      city: "Portland",
      state: "ME",
      zip: "04101",
      phone: "(207) 871-8387",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "18 miles from Freeport",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Largest facility in the region. Most appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "freeport-5",
      name: "Freeport Town Hall",
      type: "Municipal Passport Acceptance",
      address: "30 Main St",
      city: "Freeport",
      state: "ME",
      zip: "04032",
      phone: "(207) 865-4743",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "In downtown Freeport",
      website: "https://www.freeportmaine.com/",
      websiteLabel: "Freeport Town Hall",
      notes: "Town clerk services. Call ahead to confirm passport acceptance hours.",
      icon: "ri-government-line",
    },
    {
      id: "freeport-6",
      name: "Bath Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "25 Front St",
      city: "Bath",
      state: "ME",
      zip: "04530",
      phone: "(207) 443-5531",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "15 miles from Freeport",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Sagadahoc County option. Backup for Freeport area.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — drive to Boston", fpc: "No — ship from home" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Local Freeport photo shops may use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Freeport acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Freeport applicants show up with incorrect checks and have to reschedule.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Boston regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Freeport families.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Freeport travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location near Freeport." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Freeport address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Freeport, Maine?",
      a: "Fast Passport Center offers emergency passport processing in Freeport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, bypassing standard mail delays.",
    },
    {
      q: "Can I get a same-day passport in Freeport, Maine?",
      a: "Same-day passports are only available at the Boston Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option.",
    },
    {
      q: "Where can I get a fast passport renewal in Freeport, Maine?",
      a: "Fast Passport Center offers fast passport renewal in Freeport with no office visit required. Simply complete your application online, ship your documents using our prepaid overnight label, and our couriers handle the rest.",
    },
    {
      q: "How far is Freeport from the Boston Passport Agency?",
      a: "Freeport is approximately 120 miles from the Boston Passport Agency — about a 2-hour drive. With Fast Passport Center, you avoid that trip entirely.",
    },
    {
      q: "What acceptance facilities are near Freeport, Maine?",
      a: "Options near Freeport include the Freeport Post Office (04032), Brunswick Post Office (04011), Yarmouth Post Office (04096), and Freeport Town Hall (04032). The Freeport Post Office is most convenient.",
    },
    {
      q: "Do I need to visit the Boston Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery.",
    },
    {
      q: "Is Freeport close to Portland Jetport?",
      a: "Yes, Portland International Jetport (PWM) is only about 18 miles from Freeport — roughly 20 minutes away. It's the closest major airport for Freeport travelers.",
    },
    {
      q: "How much does expedited passport service cost in Freeport?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed. Contact us for a quote.",
    },
    {
      q: "What documents do I need for a first-time passport in Freeport?",
      a: "You'll need proof of citizenship, government-issued photo ID, two passport photos, and payment. We provide a complete checklist and review your documents.",
    },
    {
      q: "Can you help with a child's passport in Freeport?",
      a: "Yes. Child passports require both parents (or notarized consent), the child's birth certificate, and both parents' IDs. We walk Freeport families through the process.",
    },
    {
      q: "Is there a UPS Store near Freeport for shipping documents?",
      a: "Yes, there's a UPS Store in the Freeport Village Station area. We provide a prepaid overnight label. The Freeport Post Office on Bow St also accepts packages.",
    },
    {
      q: "Can I get passport photos taken in Freeport?",
      a: "Yes, CVS and Rite Aid in Freeport offer passport photo services. The Walgreens and CVS in Brunswick are also convenient options.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Freeport", address: "5 Bow St, Freeport, ME 04032", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Rite Aid — Freeport", address: "30 Main St, Freeport, ME 04032", hours: "Mon–Sun: 8AM–10PM" },
    { name: "CVS Pharmacy — Brunswick", address: "15 Pleasant St, Brunswick, ME 04011", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Brunswick", address: "25 Pleasant St, Brunswick, ME 04011", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walgreens — Yarmouth", address: "15 Forest Falls Dr, Yarmouth, ME 04096", hours: "Mon–Sun: 7AM–11PM" },
    { name: "CVS Pharmacy — Portland", address: "125 Forest Ave, Portland, ME 04101", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart Photo Center — Portland", address: "200 John Roberts Rd, South Portland, ME 04106", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Rite Aid — Falmouth", address: "25 Falmouth Rd, Falmouth, ME 04105", hours: "Mon–Sun: 8AM–10PM" },
  ],
  dmvLocations: [
    {
      name: "BMV — Portland",
      address: "25 Middlesex St, Portland, ME 04101",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service BMV. 18 miles from Freeport. Online appointment booking available.",
      featured: true,
    },
    {
      name: "BMV — Brunswick",
      address: "35 Pleasant St, Brunswick, ME 04011",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Sagadahoc/Cumberland option. 6 miles from Freeport.",
      featured: false,
    },
    {
      name: "BMV — Bath",
      address: "35 Front St, Bath, ME 04530",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Sagadahoc County option. 15 miles from Freeport.",
      featured: false,
    },
    {
      name: "BMV — Augusta",
      address: "29 State House Station, Augusta, ME 04333",
      hours: "Mon–Fri: 8AM–5PM",
      note: "State capital full-service BMV. 45 miles from Freeport.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PWM",
      name: "Portland International Jetport",
      distance: "18 miles from Freeport (20 min)",
      address: "1001 Westbrook St, Portland, ME 04102",
      note: "Maine's busiest airport and closest to Freeport. Direct flights to Boston, New York, Chicago, Philadelphia, and seasonal international connections. The best option for Freeport travelers.",
      flightUrl: "https://www.portlandjetport.org/",
      flightLabel: "PWM Flight Info",
      featured: true,
    },
    {
      code: "BGR",
      name: "Bangor International Airport",
      distance: "110 miles from Freeport (2 hours)",
      address: "2870 Godfrey Blvd, Bangor, ME 04401",
      note: "Maine's second busiest airport with direct flights to major hubs. Alternative for Freeport travelers.",
      flightUrl: "https://www.flybangor.com/",
      flightLabel: "Bangor Airport",
      featured: false,
    },
    {
      code: "BOS",
      name: "Logan International Airport",
      distance: "120 miles from Freeport (2 hours)",
      address: "1 Harborside Dr, Boston, MA 02128",
      note: "Major international hub with direct flights to Europe, Asia, and Latin America. Many Freeport travelers drive to Boston for international travel.",
      flightUrl: "https://www.massport.com/logan-airport",
      flightLabel: "Logan Airport",
      featured: false,
    },
  ],
};